import React, { useEffect, useState } from "react";
import { X, Copy, RefreshCw, KeyRound } from "lucide-react";
import { obterOuCriarAutenticadorZenTag, regerarAutenticadorZenTag } from "../../services/api";

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const Field = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  const doCopy = async () => {
    const ok = await copy(value || "");
    setCopied(ok);
    setTimeout(() => setCopied(false), 1200);
    if (!ok) alert("Não foi possível copiar. Copie manualmente.");
  };

  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-gray-600 dark:text-slate-300">{label}</div>
      <div className="flex items-center gap-2">
        <input
          value={value || ""}
          readOnly
          className="
            flex-1 px-3 py-2 rounded-xl border outline-none
            zen-card dark:bg-slate-950/40 dark:border-white/10 dark:text-white
          "
        />
        <button
          type="button"
          onClick={doCopy}
          className="
            inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold
            bg-gray-100 hover:bg-gray-200 text-gray-800
            dark:bg-white/10 dark:hover:bg-white/15 dark:text-white
          "
          title="Copiar"
        >
          <Copy size={16} />
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
    </div>
  );
};

const ZentagAuthModal = ({
  show,
  onClose,
  token,
  licId,
  estabelecimentoNome,
  slug,
  estabelecimentoIdFromLic,
}) => {
  const [loading, setLoading] = useState(false);
  const [auth, setAuth] = useState(null);

  const load = async () => {
    if (!licId) return;
    setLoading(true);
    try {
      const r = await obterOuCriarAutenticadorZenTag(licId, token);
      setAuth(r.data);
    } catch (e) {
      console.error(e);
      alert("Erro ao carregar/autogerar autenticador.");
      setAuth(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, licId]);

  const handleRegerar = async () => {
    if (!window.confirm("Regerar ClientId/ClientSecret? O anterior vai parar de funcionar.")) return;
    setLoading(true);
    try {
      const r = await regerarAutenticadorZenTag(licId, token);
      setAuth(r.data);
      alert("Autenticador regerado com sucesso!");
    } catch (e) {
      console.error(e);
      alert("Erro ao regerar autenticador.");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const estabName = estabelecimentoNome || "-";
  const estabId = auth?.estabelecimentoId ?? estabelecimentoIdFromLic ?? null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full max-w-xl rounded-2xl zen-card p-6 shadow-xl dark:bg-slate-950/80 dark:border-white/10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <KeyRound size={18} /> Autenticador ZenTag
            </h3>

            {/* ✅ mostra nome para evitar confusão */}
            <p className="text-sm text-gray-700 dark:text-slate-200 mt-1">
              <b>{estabName}</b>
              {slug ? <span className="text-gray-500 dark:text-slate-400"> • {slug}</span> : null}
            </p>

            <p className="text-xs text-gray-500 dark:text-slate-300/80 mt-1">
              Licença #{licId}
              {estabId ? <> • EstabelecimentoId: <b>{estabId}</b></> : null}
              {" "}• (Se não existir, será criado automaticamente.)
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10"
            title="Fechar"
          >
            <X size={18} className="text-gray-700 dark:text-white" />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          {loading && (
            <div className="text-sm text-gray-600 dark:text-slate-200">
              Carregando...
            </div>
          )}

          {!loading && auth && (
            <>
              <div className="grid grid-cols-1 gap-4">
                <Field label="ClientId" value={auth.clientId} />
                <Field label="ClientSecret" value={auth.clientSecret} />
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="text-xs text-gray-500 dark:text-slate-400">
                  {estabId ? <>EstabelecimentoId: <b>{estabId}</b></> : null}
                </div>

                <button
                  type="button"
                  onClick={handleRegerar}
                  disabled={loading}
                  className="
                    inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm
                    bg-red-600 text-white hover:bg-red-500 disabled:opacity-60
                  "
                >
                  <RefreshCw size={16} />
                  Regerar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZentagAuthModal;
